import {
  Person,
  Relationship,
  Source,
  Repository,
  Story,
  ResearchTask,
  GenealogyEvent,
  EventParticipant,
  EventSourceCitation,
} from '../../types.ts';
import { OfflineQueue } from '../OfflineQueue.ts';
import { metEigenFolio } from '../SourceUtils.ts';

// Re-export OfflineQueue for domain modules that need it
export { OfflineQueue };

// === ApiError ===

export class ApiError extends Error {
  public status: number;
  public code?: string;
  public requiredTier?: string;
  public currentTier?: string;
  public limit?: number;
  public field?: string;
  public messageKey?: string;
  public params?: Record<string, string>;
  public details?: Array<{ field: string; message: string; code: string }>;
  /** #1825 — TOO_MANY_PARENTS / SAME_GENDER_PARENT: het kind en de ouder die er al staat. */
  public childId?: string;
  public parentId?: string;
  /** #1829 — SAME_GENDER_PARENT na een geslachtswijziging: de persoon wiens geslacht wijzigde. */
  public personId?: string;
  /** #1825 — een 403 die het venster zelf meldt (zie ZelfMelden): er kwam geen globale toast. */
  public zelfGemeld?: boolean;
  /** #1915 — waarom een kortingscode niet bruikbaar is (COUPON_INVALID). */
  public reden?: string;

  constructor(message: string, status: number, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = data?.code as string | undefined;
    this.requiredTier = data?.requiredTier as string | undefined;
    this.currentTier = data?.currentTier as string | undefined;
    this.limit = data?.limit as number | undefined;
    this.field = data?.field as string | undefined;
    this.messageKey = data?.messageKey as string | undefined;
    this.params = data?.params as Record<string, string> | undefined;
    this.details = data?.details as
      Array<{ field: string; message: string; code: string }> | undefined;
    this.childId = data?.childId as string | undefined;
    this.parentId = data?.parentId as string | undefined;
    this.personId = data?.personId as string | undefined;
    this.reden = data?.reden as string | undefined;
  }
}

// === Error handling ===

/**
 * #1825 — een venster dat weet wat er wel en niet is opgeslagen, geeft één melding met de
 * reden en wat nu, ook bij een 403 (abonnementslimiet, geen rechten). Zo'n verzoek geeft
 * `zelfMelden: true` mee: de 403 krijgt dan geen globale toast maar `zelfGemeld`, en het
 * venster meldt hem. Per verzoek, zodat een 403 van een ander verzoek dat tegelijk loopt
 * gewoon de globale toast krijgt.
 */
export type ZelfMelden = { zelfMelden?: boolean };

export async function parseErrorResponse(response: Response, opts?: ZelfMelden): Promise<never> {
  let data: Record<string, unknown> = {};
  try {
    data = await response.json();
  } catch {
    data = { error: response.statusText };
  }
  const fout = new ApiError(
    String(data.error || `Request failed (${response.status})`),
    response.status,
    data
  );
  if (response.status === 403) {
    if (opts?.zelfMelden) fout.zelfGemeld = true;
    else
      // #1857 — soort limiet, grens en vereiste tier gaan mee: App.tsx toont daar een
      // venster met de echte reden in plaats van "geen bewerkrechten".
      window.dispatchEvent(
        new CustomEvent('pecktree:permission-denied', {
          detail: {
            code: data.code,
            kind: data.kind,
            limit: data.limit,
            requiredTier: data.requiredTier,
            role: data.role,
          },
        })
      );
  }
  throw fout;
}

/**
 * True voor een 403 die de globale handler in App.tsx al toonde: roep dan geen eigen toast aan.
 * Met `zelfMelden` is dat niet zo; dan meldt het venster hem.
 */
export const isGlobaalGemeld = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 403 && !err.zelfGemeld;

// === Timeouts ===

export const FETCH_TIMEOUT = 5000;
export const BULK_FETCH_TIMEOUT = 60000; // 60s for bulk import operations

/**
 * Registratie en het opnieuw sturen van een activatiemail (#1402).
 *
 * Deze twee hashen een wachtwoord met bcrypt cost 12 en raken de database een
 * paar keer. Met de standaard van 5 s brak de browser af zodra het net wat
 * langer duurde — en toonde dan de rauwe melding "aborted" aan iemand die net
 * een account probeerde te maken.
 */
export const REGISTER_TIMEOUT = 15000;

// === Backend config ===

// Cache the backend config check result to avoid repeated API calls
let _backendConfigCache: { hasDatabase: boolean; version?: string } | null = null;
let _backendConfigPromise: Promise<void> | null = null;

// Call this once during app initialization to check backend config
export const initializeBackendConfig = async (): Promise<{
  hasDatabase: boolean;
  version?: string;
}> => {
  if (_backendConfigPromise) {
    await _backendConfigPromise;
    return _backendConfigCache || { hasDatabase: false };
  }

  _backendConfigPromise = (async () => {
    try {
      const response = await fetch('/api/config');
      if (response.ok) {
        _backendConfigCache = await response.json();
      } else {
        _backendConfigCache = { hasDatabase: false };
      }
    } catch (error) {
      console.warn('⚠️  Could not load backend config:', error);
      _backendConfigCache = { hasDatabase: false };
    }
  })();

  await _backendConfigPromise;
  return _backendConfigCache || { hasDatabase: false };
};

// === Local storage helpers ===

export const getUserId = () => {
  const user = JSON.parse(localStorage.getItem('af_user') || '{}');
  return user.id || 'anonymous';
};

export const getLocalData = (key: string) => {
  try {
    const data = localStorage.getItem(`af_offline_${key}`);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
};

export const setLocalData = (key: string, data: unknown) => {
  localStorage.setItem(`af_offline_${key}`, JSON.stringify(data));
};

// === CSRF ===

// Read CSRF token from cookie for state-changing requests
function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? match[1] : '';
}

// === Token refresh ===

// Track if a refresh is in progress to avoid concurrent refresh requests
let _refreshPromise: Promise<boolean> | null = null;

export async function tryRefreshToken(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// === fetchWithTimeout ===

export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout: number = FETCH_TIMEOUT
) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  // Add CSRF token header for state-changing methods
  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    options.headers = {
      ...options.headers,
      'x-csrf-token': getCsrfToken(),
    };
  }

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Always send httpOnly cookie
      signal: controller.signal,
    });
    clearTimeout(id);

    // Auto-refresh on 401 (expired access token)
    if (response.status === 401 && !url.includes('/api/auth/')) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry the original request with new access token
        // #1769 — met de limiet van de aanroeper; een import na een verlopen
        // token brak anders alsnog na 5 s af.
        const retryController = new AbortController();
        const retryId = setTimeout(() => retryController.abort(), timeout);
        try {
          const retryResponse = await fetch(url, {
            ...options,
            credentials: 'include',
            signal: retryController.signal,
          });
          clearTimeout(retryId);
          return retryResponse;
        } catch (error) {
          clearTimeout(retryId);
          throw error;
        }
      } else {
        // Refresh failed — session is truly expired, notify the app
        window.dispatchEvent(new CustomEvent('pecktree:session-expired'));
      }
    }

    return response;
  } catch (error) {
    clearTimeout(id);
    // Een afgebroken fetch komt binnen als een DOMException met een tekst die
    // per browser verschilt ("The user aborted a request", "signal is aborted
    // without reason"). Die tekst kwam ongefilterd in beeld te staan. Hier
    // wordt het één herkenbare fout met een vertaalsleutel (#1402).
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('De server reageerde niet op tijd.', 0, {
        messageKey: 'error_request_timeout',
        code: 'REQUEST_TIMEOUT',
      });
    }
    throw error;
  }
}

// === Proactive token refresh ===

// Proactive token refresh: refresh the access token before it expires
// JWT_EXPIRES_IN is 15m, so refresh at 13m to avoid edge cases
const PROACTIVE_REFRESH_MS = 13 * 60 * 1000;
let _refreshTimer: ReturnType<typeof setInterval> | null = null;

export function startProactiveRefresh() {
  stopProactiveRefresh();
  _refreshTimer = setInterval(() => {
    tryRefreshToken().catch((err) =>
      console.warn('proactive token refresh failed:', err.message || err)
    );
  }, PROACTIVE_REFRESH_MS);
}

export function stopProactiveRefresh() {
  if (_refreshTimer) {
    clearInterval(_refreshTimer);
    _refreshTimer = null;
  }
}

export async function ensureSession(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (res.ok) return true;
    if (res.status === 401) {
      return await tryRefreshToken();
    }
    return false;
  } catch {
    return false;
  }
}

export async function refreshIfNeeded(): Promise<boolean> {
  return tryRefreshToken();
}

export function getErrorMessage(
  error: unknown,
  t: (key: string, params?: Record<string, string>) => string
): string {
  if (error instanceof ApiError) {
    // Prefer messageKey (specific) over code-based key (generic)
    if (error.messageKey) {
      const translated = t(error.messageKey, error.params);
      if (translated !== error.messageKey) return translated;
    }
    // Fallback: try error_CODE (e.g. error_INVALID_CREDENTIALS)
    if (error.code) {
      const key = `error_${error.code}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
  }
  // #1756 — fetch geeft bij een netwerkfout een TypeError met browsertekst
  // ("Failed to fetch", "Load failed", "NetworkError…"); die hoort niet in beeld.
  if (error instanceof TypeError) return t('connectionError');
  if (error instanceof Error) return error.message;
  return String(error);
}

// === Server row type (snake_case DB rows with dynamic shape) ===

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ServerRow = Record<string, any>;

// === Field mapping: frontend camelCase <-> server snake_case ===

export const genderToServer = (g?: string): string => {
  switch (g) {
    case 'M':
      return 'male';
    case 'F':
      return 'female';
    default:
      return 'unknown';
  }
};

export const genderFromServer = (g?: string): string => {
  switch (g) {
    case 'male':
      return 'M';
    case 'female':
      return 'F';
    default:
      return 'U';
  }
};

export const relTypeToServer = (t?: string): string => {
  switch (t) {
    case 'spousal':
      return 'spouse';
    case 'parent-child':
      return 'parent';
    case 'adoptive':
      return 'adoptive_parent';
    case 'foster':
      return 'foster_parent';
    default:
      return t || 'parent';
  }
};

export const relTypeFromServer = (t?: string): string => {
  switch (t) {
    case 'spouse':
      return 'spousal';
    case 'parent':
      return 'parent-child';
    case 'adoptive_parent':
      return 'adoptive';
    case 'foster_parent':
      return 'foster';
    default:
      return t || 'parent-child';
  }
};

export const personToServer = (person: Partial<Person>): Record<string, any> => {
  const payload: Record<string, any> = {};
  if (person.givenNames !== undefined) payload.first_name = person.givenNames;
  if (person.surnamePrefix !== undefined) payload.surname_prefix = person.surnamePrefix;
  // Refs #781 — patronym (Pietersz, Lambertsdr); round-trippable via GEDCOM 2 _PATR
  if (person.patronym !== undefined) payload.patronym = person.patronym;
  if (person.surname !== undefined) payload.last_name = person.surname;
  // A-23 — terugsturen zodat een save de geïmporteerde meisjesnaam niet wist.
  if (person.maidenName !== undefined) payload.maiden_name = person.maidenName;
  if (person.gender !== undefined) payload.gender = genderToServer(person.gender);
  if (person.birthDate !== undefined) payload.birth_date = person.birthDate;
  if (person.birthPlace !== undefined) payload.birth_place = person.birthPlace;
  if (person.birthPlaceId !== undefined) payload.birth_place_id = person.birthPlaceId;
  if (person.deathDate !== undefined) payload.death_date = person.deathDate;
  if (person.deathPlace !== undefined) payload.death_place = person.deathPlace;
  if (person.deathPlaceId !== undefined) payload.death_place_id = person.deathPlaceId;
  if (person.baptismDate !== undefined) payload.baptism_date = person.baptismDate;
  if (person.baptismPlace !== undefined) payload.baptism_place = person.baptismPlace;
  if (person.baptismPlaceId !== undefined) payload.baptism_place_id = person.baptismPlaceId;
  if (person.burialDate !== undefined) payload.burial_date = person.burialDate;
  if (person.burialPlace !== undefined) payload.burial_place = person.burialPlace;
  if (person.burialPlaceId !== undefined) payload.burial_place_id = person.burialPlaceId;
  // occupation / primaryOccupationEventId (#616) — de backend kiest welke
  // het verwerkt: primaryOccupationEventId wint, occupation-string is een
  // legacy fallback die de backend via match-or-create omzet naar een event.
  if (person.occupation !== undefined) payload.occupation = person.occupation;
  if (person.primaryOccupationEventId !== undefined)
    payload.primaryOccupationEventId = person.primaryOccupationEventId;
  if (person.religion !== undefined) payload.religion = person.religion;
  // A-04 — `nationality` ontbrak hier én in `personFromServer`, terwijl de
  // persoon-editor er een invoerveld voor heeft (PePersonalSection.tsx:140) en de
  // kolom in de database bestaat. Wat de genealoog invulde werd nooit verstuurd en
  // wat er stond kwam nooit terug: een veld dat er is maar niets doet.
  if (person.nationality !== undefined) payload.nationality = person.nationality;
  if (person.biography !== undefined) payload.biography = person.biography;
  if (person.notes !== undefined) payload.notes = person.notes;
  // #1583 — de rapporttekst; '' = leegmaken (de server maakt er NULL van).
  if (person.reportText !== undefined) payload.report_text = person.reportText;
  if (person.isLiving !== undefined) payload.is_living = person.isLiving;
  if (person.isLivingHandmatig !== undefined)
    payload.is_living_handmatig = person.isLivingHandmatig;
  if (person.photoUrl !== undefined) payload.photo_url = person.photoUrl;
  if (person.title !== undefined) payload.title = person.title;
  if (person.nickname !== undefined) payload.nickname = person.nickname;
  if (person.prefix !== undefined) payload.prefix = person.prefix;
  if (person.suffix !== undefined) payload.suffix = person.suffix;
  if (person.causeOfDeath !== undefined) payload.cause_of_death = person.causeOfDeath;
  if (person.birthUnknown !== undefined) payload.birth_unknown = person.birthUnknown;
  if (person.deathUnknown !== undefined) payload.death_unknown = person.deathUnknown;
  if (person.refNumber !== undefined) payload.ref_number = person.refNumber;
  if (person.facts !== undefined) {
    // #619 Fase E: stuur het facts-object (niet een JSON-string). De backend
    // normaliseert + wrapt in {facts:...} via normalizeFacts(). Eerder werd
    // dit als JSON-string verstuurd, wat in combinatie met route-side
    // JSON.stringify leidde tot JSONB string-type in de DB (double encoding).
    payload.facts = person.facts || {};
  }
  // Events worden NIET meer via custom_facts meegestuurd — ze gaan via de events API
  if (person.alternativeNames !== undefined) payload.alternative_names = person.alternativeNames;
  if (person.sourceIds !== undefined) payload.source_ids = person.sourceIds;
  if (person.restriction !== undefined) payload.restriction = person.restriction;
  if (person.isPublic !== undefined) payload.is_public = person.isPublic;
  // #1683 — GEDCOM `_UID`/`EXID` heen en terug; anders krijgt een persoon bij
  // export steeds een nieuwe UID en herkent een ander programma hem niet meer.
  if (person.uid !== undefined) payload.uid = person.uid;
  if (person.externalId !== undefined) payload.external_id = person.externalId;
  if (person.researchCompleted !== undefined) payload.research_completed = person.researchCompleted;
  if (person.updatedAt !== undefined) payload.expected_version = person.updatedAt;
  return payload;
};

export const parseCustomFacts = (raw: unknown): { facts: Record<string, unknown> } => {
  if (!raw) return { facts: {} };
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (!parsed || typeof parsed !== 'object') return { facts: {} };
  const obj = parsed as Record<string, unknown>;
  return { facts: (obj.facts as Record<string, unknown>) || {} };
};

export const personFromServer = (row: ServerRow): Person => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  givenNames: row.first_name || row.givenNames || '',
  surnamePrefix: row.surname_prefix || row.surnamePrefix || '',
  // Refs #781 — patronym (Pietersz, Lambertsdr); GEDCOM 2 _PATR custom tag.
  patronym: row.patronym || '',
  surname: row.last_name || row.surname || '',
  /**
   * A-23 — `people.maiden_name` was een weeskolom: wél in élke SELECT, wél
   * beschrijfbaar via normalizePersonData, maar nergens in de client gemapt.
   * `personMatchesQuery` (nameUtils) en PersonManager zoeken er allebei naar en
   * vonden dus altijd niets; een GEDCOM-import die de meisjesnaam daar neerlegt
   * toonde hem nergens en exporteerde hem niet. In Nederlands onderzoek is de
   * meisjesnaam de primaire identificatie van een vrouw — dat mag niet in een
   * onzichtbare kolom belanden.
   *
   * Bewust GEEN tweede invoerveld erbij: de app legt nieuwe meisjesnamen vast
   * als `alternativeNames` met `type: 'maiden'`, en een tweede plek om hetzelfde
   * in te typen maakt precies de derde waarheid waar deze bevinding voor
   * waarschuwt. Deze mapping maakt bestaande data zichtbaar en doorzoekbaar.
   */
  maidenName: row.maiden_name || row.maidenName || undefined,
  gender:
    row.gender === 'male' || row.gender === 'female' || row.gender === 'unknown'
      ? (genderFromServer(row.gender) as Person['gender'])
      : row.gender || 'U',
  birthDate: row.birth_date || row.birthDate || '',
  birthPlace: row.birth_place || row.birthPlace || '',
  birthPlaceId: row.birth_place_id || row.birthPlaceId || null,
  birthMunicipality: row.birth_municipality || row.birthMunicipality || '',
  birthState: row.birth_state || row.birthState || '',
  birthCountry: row.birth_country || row.birthCountry || '',
  deathDate: row.death_date || row.deathDate || '',
  deathPlace: row.death_place || row.deathPlace || '',
  deathPlaceId: row.death_place_id || row.deathPlaceId || null,
  // NC #723 — provincie/land van de overlijdensplaats uit de places-tabel, net als bij geboorte.
  deathMunicipality: row.death_municipality || row.deathMunicipality || '',
  deathState: row.death_state || row.deathState || '',
  deathCountry: row.death_country || row.deathCountry || '',
  baptismDate: row.baptism_date || row.baptismDate || '',
  baptismPlace: row.baptism_place || row.baptismPlace || '',
  baptismPlaceId: row.baptism_place_id || row.baptismPlaceId || null,
  // #1550 (NC #783) — voor de doop/begrafenis-terugval, net als birth/death hierboven.
  baptismMunicipality: row.baptism_municipality || row.baptismMunicipality || '',
  baptismState: row.baptism_state || row.baptismState || '',
  baptismCountry: row.baptism_country || row.baptismCountry || '',
  burialDate: row.burial_date || row.burialDate || '',
  burialPlace: row.burial_place || row.burialPlace || '',
  burialPlaceId: row.burial_place_id || row.burialPlaceId || null,
  burialMunicipality: row.burial_municipality || row.burialMunicipality || '',
  burialState: row.burial_state || row.burialState || '',
  burialCountry: row.burial_country || row.burialCountry || '',
  isLiving: row.is_living ?? row.isLiving ?? !row.death_date,
  isLivingHandmatig: row.is_living_handmatig ?? row.isLivingHandmatig ?? false,
  photoUrl: row.photo_url || row.photoUrl,
  biography: row.biography,
  occupation: row.occupation,
  primaryOccupationEventId: row.primary_occupation_event_id || row.primaryOccupationEventId || null,
  religion: row.religion,
  nationality: row.nationality,
  title: row.title,
  nickname: row.nickname,
  birthUnknown: row.birth_unknown ?? row.birthUnknown ?? false,
  deathUnknown: row.death_unknown ?? row.deathUnknown ?? false,
  refNumber: row.ref_number || row.refNumber,
  notes: row.notes,
  reportText: row.report_text ?? row.reportText,
  prefix: row.prefix,
  suffix: row.suffix,
  sosaNumber: row.sosa_number ?? row.sosaNumber ?? null,
  causeOfDeath: row.cause_of_death || row.causeOfDeath,
  sourceIds: row.source_ids
    ? typeof row.source_ids === 'string'
      ? JSON.parse(row.source_ids)
      : row.source_ids
    : row.sourceIds || [],
  // #1318 — de vindplaats binnen elke bron (folio, aktenummer), per bron-id.
  // Los van sourceIds gehouden: dat veld wordt op tientallen plekken als platte
  // lijst gebruikt en hoeft hier niets van te weten.
  sourcePages: row.source_pages
    ? typeof row.source_pages === 'string'
      ? JSON.parse(row.source_pages)
      : row.source_pages
    : row.sourcePages || {},
  // A-02 — NIET naar '' normaliseren: `personToServer` stuurt élke gedefinieerde
  // waarde mee en de server schrijft die weg. Een afwezige restrictie werd zo bij
  // de eerstvolgende save als lege string weggeschreven (38 rijen in de dev-DB),
  // en dat wiste "Vertrouwelijk" — precies de vlag waar de privacyfilter op stuurt.
  restriction: row.restriction || undefined,
  isPublic: row.is_public ?? row.isPublic ?? undefined,
  uid: row.uid || undefined,
  externalId: row.external_id || row.externalId || undefined,
  researchCompleted: row.research_completed ?? row.researchCompleted ?? undefined,
  // Privacy-vlag van de server doorgeven; zonder dit is elke client-check erop dood.
  _privacy_redacted: row._privacy_redacted === true ? true : undefined,
  // B-30 — de kolom werd al geselecteerd maar viel hier weg, waardoor "recent
  // toegevoegd" niet op tijd kon sorteren.
  createdAt: row.created_at || row.createdAt || undefined,
  events: (row.events || row.events_json || []).map((ev: ServerRow) => eventFromServer(ev)),
  facts: parseCustomFacts(row.custom_facts).facts as Person['facts'],
  alternativeNames: (() => {
    if (!row.alternative_names) return [];
    const parsed =
      typeof row.alternative_names === 'string'
        ? JSON.parse(row.alternative_names)
        : row.alternative_names;
    return Array.isArray(parsed) ? parsed : [];
  })(),
  updatedAt: row.updated_at || row.updatedAt,
});

export const relationshipToServer = (rel: Partial<Relationship>): Record<string, any> => ({
  person_id: rel.personA,
  related_person_id: rel.personB,
  type: relTypeToServer(rel.type),
  notes: rel.notes,
  source_ids: rel.sourceIds || [],
  relation_subtype: rel.relationSubtype ?? null,
  // A-07 — CONSTRAINT-013: een leeg veld uit de UI betekent "wissen", maar
  // `undefined` betekent "niet aangeraakt". Alleen meesturen wat de caller
  // daadwerkelijk heeft gezet.
  // Lege string = bewust gewist (de server schrijft dan NULL weg); weglaten =
  // niet aangeraakt. Zie de CASE in repos/relationships.js:updateRow.
  ...(rel.startDate !== undefined ? { start_date: rel.startDate || '' } : {}),
  ...(rel.endDate !== undefined ? { end_date: rel.endDate || '' } : {}),
  ...((rel as any).events ? { events: (rel as any).events } : {}),
  // #1828 — de keuze voor de gebeurtenis bij een soortwissel (routes/relationships.js).
  ...(rel.partnerEventAction ? { partner_event_action: rel.partnerEventAction } : {}),
});

export const relationshipFromServer = (row: ServerRow): Relationship => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  personA: row.person_id || row.personA || '',
  personB: row.related_person_id || row.personB || '',
  type:
    row.type === 'spouse' ||
    row.type === 'parent' ||
    row.type === 'adoptive_parent' ||
    row.type === 'foster_parent'
      ? (relTypeFromServer(row.type) as Relationship['type'])
      : row.type || 'parent-child',
  notes: row.notes,
  sourceIds: row.source_ids
    ? typeof row.source_ids === 'string'
      ? JSON.parse(row.source_ids)
      : row.source_ids
    : [],
  relationSubtype: row.relation_subtype || undefined,
  // A-07 — de kolommen bestonden al; alleen de mappers kenden ze niet.
  startDate: row.start_date || row.startDate || undefined,
  endDate: row.end_date || row.endDate || undefined,
});

export const sourceToServer = (source: Partial<Source>): Record<string, any> => {
  const payload: Record<string, any> = {};
  if (source.title !== undefined) payload.title = source.title;
  if (source.author !== undefined) payload.author = source.author;
  if (source.repositoryId !== undefined) payload.repository_id = source.repositoryId;
  if (source.callNumber !== undefined) payload.call_number = source.callNumber;
  if (source.scanNumber !== undefined) payload.scanNumber = source.scanNumber;
  if (source.mediaType !== undefined) payload.media_type = source.mediaType;
  if (source.url !== undefined) payload.url = source.url;
  if (source.type !== undefined) payload.source_type = source.type;
  if (source.reliability !== undefined) payload.reliability = source.reliability;
  if (source.description !== undefined) payload.notes = source.description;
  if (source.citation !== undefined) payload.citation = source.citation;
  if (source.citationFields !== undefined) payload.citation_fields = source.citationFields;
  if (source.originalTitle !== undefined) payload.originalTitle = source.originalTitle;
  // #823 — null = wissen, undefined = niet meesturen (server behoudt).
  if (source.archiefbezoekStatus !== undefined)
    payload.archiefbezoekStatus = source.archiefbezoekStatus;
  if (source.archiefbezoekNotitie !== undefined)
    payload.archiefbezoekNotitie = source.archiefbezoekNotitie;
  return payload;
};

export const sourceFromServer = (row: ServerRow): Source => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  title: row.title || '',
  author: row.author,
  repositoryId: row.repository_id || row.repositoryId,
  callNumber: row.call_number || row.callNumber,
  scanNumber: row.scan_number || row.scanNumber || undefined,
  mediaType: row.media_type || row.mediaType,
  url: row.url,
  type: row.source_type || row.type || 'Other',
  reliability: row.reliability || 'unknown',
  description: row.notes || row.description,
  citation: row.citation || undefined,
  // Besluit #1962 — de folio uit de akte in het eigen folioveld van het brontype.
  citationFields: metEigenFolio(
    row.source_type || row.type || 'Other',
    typeof row.citation_fields === 'string'
      ? JSON.parse(row.citation_fields)
      : row.citation_fields || {}
  ),
  verificationStatus: row.verification_status ?? undefined,
  verifiedAt: row.verified_at ?? undefined,
  archiefbezoekStatus: row.archiefbezoek_status ?? undefined,
  archiefbezoekNotitie: row.archiefbezoek_notitie ?? undefined,
  archiefbezoekBijgewerkt: row.archiefbezoek_bijgewerkt ?? undefined,
  completenessScore: row.completeness_score != null ? Number(row.completeness_score) : undefined,
  originalTitle: row.original_title || undefined,
  createdAt: row.created_at || row.createdAt || undefined,
});

export const storyToServer = (story: Partial<Story>): Record<string, any> => {
  const payload: Record<string, any> = {};
  if (story.title !== undefined) payload.title = story.title;
  if (story.content !== undefined) payload.content = story.content;
  if (story.taggedPersonIds !== undefined) payload.linked_people = story.taggedPersonIds;
  if (story.isDraft !== undefined) payload.is_draft = story.isDraft;
  if (story.type !== undefined) payload.type = story.type;
  if (story.location !== undefined) payload.location = story.location || null;
  if (story.date !== undefined) payload.date = story.date || null;
  if (story.isAiGenerated !== undefined) payload.is_ai_generated = story.isAiGenerated;
  if (story.mentionedPersonIds !== undefined) payload.mentioned_people = story.mentionedPersonIds;
  // C-16 — citaties bij het verhaal. Alleen meesturen als het scherm ze kent;
  // een lege array is wel een wis-opdracht, `undefined` is dat niet.
  if (story.sourceIds !== undefined) payload.source_ids = story.sourceIds;
  return payload;
};

/**
 * De personen bij een verhaal komen als id-strings binnen — behalve waar ze dat
 * niet doen.
 *
 * `stories.linked_people` is een losse JSONB-kolom. De app schrijft er
 * id-strings in, maar in bestaande data (o.a. de demo-seed) staan
 * `{ id, name }`-objecten. Elke consument — het verhalenscherm, de kaart, de
 * plaatsen, het rapport — leest de elementen als id en vergelijkt ze met
 * `person.id`; bij een object mislukt dat stil en lijkt het verhaal aan niemand
 * gekoppeld.
 *
 * Daarom hier, op de enige plek waar alle verhalen binnenkomen, en niet bij elke
 * lezer apart (NC #680).
 */
const persoonIds = (waarde: unknown): string[] =>
  Array.isArray(waarde)
    ? waarde
        .map((element) =>
          typeof element === 'string' ? element : ((element as { id?: string })?.id ?? '')
        )
        .filter(Boolean)
    : [];

export const storyFromServer = (row: ServerRow): Story => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  title: row.title || '',
  content: row.content || '',
  date: row.date,
  location: row.location,
  taggedPersonIds: persoonIds(row.linked_people ?? row.taggedPersonIds),
  mentionedPersonIds: persoonIds(row.mentioned_people),
  isDraft: row.is_draft ?? false,
  type: row.type || 'general',
  isAiGenerated: row.is_ai_generated ?? false,
  sourceIds: row.source_ids || [],
  created_at: row.created_at || '',
});

export const taskToServer = (task: Partial<ResearchTask>): Record<string, any> => {
  const payload: Record<string, any> = {};
  if (task.title !== undefined) payload.title = task.title;
  if (task.description !== undefined) payload.description = task.description;
  if (task.status !== undefined) payload.status = task.status;
  if (task.priority !== undefined) payload.priority = task.priority;
  if (task.personId !== undefined) payload.assigned_person_id = task.personId;
  if (task.assignedUserId !== undefined) payload.assigned_user_id = task.assignedUserId || null;
  if (task.dueDate !== undefined) payload.due_date = task.dueDate || null;
  if (task.completedAt !== undefined) payload.completed_at = task.completedAt || null;
  if (task.columnId !== undefined) payload.column_id = task.columnId || null;
  if (task.result !== undefined) payload.result = task.result || null;
  if (task.sourceId !== undefined) payload.source_id = task.sourceId || null;
  if (task.personIds !== undefined) payload.person_ids = task.personIds;
  if (task.sourceIds !== undefined) payload.source_ids = task.sourceIds;
  return payload;
};

export const taskFromServer = (row: ServerRow): ResearchTask => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  title: row.title || '',
  description: row.description || '',
  status: row.status || 'todo',
  priority: row.priority || 'medium',
  personId: row.assigned_person_id || row.personId,
  assignedUserId: row.assigned_user_id || '',
  columnId: row.column_id || '',
  position: row.position || 0,
  dueDate: row.due_date || '',
  created_at: row.created_at || '',
  completedAt: row.completed_at || '',
  deletedAt: row.deleted_at || '',
  commentCount: parseInt(row.comment_count) || 0,
  checklistTotal: parseInt(row.checklist_total) || 0,
  checklistDone: parseInt(row.checklist_done) || 0,
  result: row.result || undefined,
  sourceId: row.source_id || undefined,
  personIds: Array.isArray(row.person_ids)
    ? row.person_ids
    : row.person_ids
      ? row.person_ids.split(',').filter(Boolean)
      : [],
  sourceIds: Array.isArray(row.source_ids)
    ? row.source_ids
    : row.source_ids
      ? row.source_ids.split(',').filter(Boolean)
      : [],
});

export const columnFromServer = (row: ServerRow): import('../../types.ts').TaskColumn => ({
  id: row.id,
  projectId: row.project_id || '',
  title: row.title || '',
  color: row.color || '#1A2F23',
  position: row.position || 0,
  isDoneColumn: row.is_done_column || false,
});

export const checklistItemFromServer = (
  row: ServerRow
): import('../../types.ts').TaskChecklistItem => ({
  id: row.id,
  taskId: row.task_id || '',
  title: row.title || '',
  isCompleted: row.is_completed || false,
  position: row.position || 0,
});

export const repositoryToServer = (repo: Partial<Repository>): Record<string, any> => {
  const payload: Record<string, any> = {};
  if (repo.name !== undefined) payload.name = repo.name;
  if (repo.address !== undefined) payload.address = repo.address;
  if (repo.website !== undefined) payload.website = repo.website;
  if (repo.email !== undefined) payload.email = repo.email;
  if (repo.phone !== undefined) payload.phone = repo.phone;
  if (repo.notes !== undefined) payload.notes = repo.notes;
  return payload;
};

export const repositoryFromServer = (row: ServerRow): Repository => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  name: row.name || '',
  address: row.address,
  website: row.website,
  email: row.email,
  phone: row.phone,
  notes: row.notes,
});

// === Event Mappers ===

export const participantFromServer = (
  row: ServerRow
): import('../../types.ts').EventParticipant => ({
  id: row.id,
  eventId: row.event_id || row.eventId || '',
  personId: row.person_id || row.personId,
  personName: row.person_name || row.personName,
  role: row.role || 'witness',
  age: row.age,
  notes: row.notes,
});

export const citationFromServer = (
  row: ServerRow
): import('../../types.ts').EventSourceCitation => ({
  id: row.id,
  eventId: row.event_id || row.eventId || '',
  sourceId: row.source_id || row.sourceId || '',
  page: row.page,
  quality: row.quality != null ? Number(row.quality) : undefined,
  dataDate: row.data_date || row.dataDate,
  dataText: row.data_text || row.dataText,
  notes: row.notes,
});

export const eventFromServer = (row: ServerRow): GenealogyEvent => ({
  id: row.id,
  projectId: row.project_id || row.projectId || '',
  type: row.type || 'other',
  gedcomTag: row.gedcom_tag || row.gedcomTag,
  subtype: row.subtype,
  title: row.title,
  date: row.date,
  dateSortYear: row.date_sort != null ? Number(row.date_sort) : undefined,
  placeName: row.place_name || row.placeName,
  placeId: row.place_id || row.placeId,
  address: row.address,
  description: row.description,
  cause: row.cause,
  agency: row.agency,
  religion: row.religion,
  restriction: row.restriction,
  sortOrder: row.sort_order != null ? Number(row.sort_order) : undefined,
  isNegated: row.is_negated ?? row.isNegated ?? false,
  participants: Array.isArray(row.participants) ? row.participants.map(participantFromServer) : [],
  sources: Array.isArray(row.sources) ? row.sources.map(citationFromServer) : [],
  // Leid sourceIds af uit de citations-array zodat client-state consistent is.
  // PersonEditor's bulk-save stuurt event.sourceIds mee naar saveEventWithCache;
  // zonder deze mapping was sourceIds undefined na een reload en werden
  // eerder gekoppelde bronnen door de volgende save gewist.
  // Fallback op row.sourceIds (plain id-array): list-fetch (peopleRepo.findByProject)
  // geeft die als platte array i.p.v. citations objects (Refs Nextcloud #516).
  sourceIds: Array.isArray(row.sources)
    ? row.sources.map((s: ServerRow) => s.source_id || s.sourceId || '').filter((id: string) => id)
    : Array.isArray(row.sourceIds)
      ? (row.sourceIds as string[]).filter((id) => !!id)
      : [],
  createdAt: row.created_at || row.createdAt,
  updatedAt: row.updated_at || row.updatedAt,
});

export const mediaToServer = (params: {
  name: string;
  data: string;
  mimeType: string;
  folderId?: string | null;
  sourceId?: string | null;
}): Record<string, any> => ({
  filename: params.name,
  fileData: params.data,
  mime_type: params.mimeType,
  folder_id: params.folderId || null,
  source_id: params.sourceId || null,
});
